import Image from 'next/image';
import Link from 'next/link';
import { useSession } from 'next-auth/react';
import MarketingLayout from '../components/MarketingLayout';

const FEATURES = [
  {
    number: 1,
    title: 'Set your 10x goals',
    body:
      "Write down the ambitious outcomes you're working toward. Not the safe ones — the ones that excite and scare you. Revisiting them daily keeps your compass pointed in the right direction.",
  },
  {
    number: 2,
    title: 'Choose your daily anchor',
    body:
      'Pick a quote, a mantra, or a principle that resonates today. It could be from a book, a mentor, or something you wrote yourself. One sentence to set your mental frame.',
  },
  {
    number: 3,
    title: 'Set targets that stretch you',
    body:
      "Define today's specific targets. The trick: aim higher than feels comfortable. You'll surprise yourself with what you can do when you stop sandbagging your own potential.",
  },
  {
    number: 4,
    title: 'Log your wins',
    body:
      'As the day unfolds, capture what went right. Completed a hard task? Solved a tricky problem? Had a great conversation? Small wins compound into massive momentum.',
  },
  {
    number: 5,
    title: 'Reflect and recalibrate',
    body:
      'End the day by revisiting your goals. Add new ones. Adjust your approach. The daily loop of plan-execute-reflect is where real growth happens.',
  },
];

const READING_LIST = [
  {
    href: 'https://superframeworks.com/blog/10x-easier-than-2x',
    badge: 'Mindset',
    badgeClasses: 'text-green-800 bg-green-100',
    title: 'Why 10x Goals Are Easier Than 2x Goals',
    source: "Superframeworks · Based on Dan Sullivan's work",
    body:
      'The paradox at the heart of 10x thinking: massive goals force you to reimagine your approach entirely, while modest goals trap you in incremental optimization.',
  },
  {
    href: 'https://antirez.com/news/112',
    badge: 'Engineering',
    badgeClasses: 'text-blue-800 bg-blue-100',
    title: 'The Mythical 10x Programmer',
    source: 'Salvatore Sanfilippo (antirez) · Creator of Redis',
    body:
      'The creator of Redis argues that 10x performance is real because programming is a design discipline where experience and knowledge multiply rather than add.',
  },
  {
    href: 'https://blog.google/alphabet/we-choose-go-moon-lessons-x-spirit-original-moonshot',
    badge: 'Moonshot',
    badgeClasses: 'text-orange-800 bg-orange-100',
    title: 'Lessons from X: In the Spirit of the Original Moonshot',
    source: 'Google / Alphabet · X (formerly Google X)',
    body:
      "How Google X's “moonshot factory” applies 10x thinking to develop world-changing technologies — and why aiming for 500 mpg beats optimizing for 50.",
  },
  {
    href: 'https://jdmeier.com/what-you-need-to-know-about-10x/',
    badge: 'Productivity',
    badgeClasses: 'text-yellow-800 bg-yellow-100',
    title: 'What You Need to Know About 10X',
    source: 'JD Meier · Author & Productivity Expert',
    body:
      'A comprehensive overview of 10x thinking as a mindset: how it drives exponential growth, creates prioritization filters, and inspires bold action.',
  },
  {
    href: 'https://erikbern.com/2016/01/08/i-believe-in-the-10x-engineer-but',
    badge: 'Engineering',
    badgeClasses: 'text-blue-800 bg-blue-100',
    title: 'I Believe in the 10x Engineer, But...',
    source: 'Erik Bernhardsson · Former CTO at Better.com',
    body:
      '“The easiest way to be a 10x engineer is to make 10 other engineers 2x more efficient.” A nuanced take on individual impact vs. team multipliers.',
  },
  {
    href: 'https://tim.blog/2018/06/26/the-tim-ferriss-show-transcripts-astro-teller-how-to-think-10x-bigger/',
    badge: 'Moonshot',
    badgeClasses: 'text-orange-800 bg-orange-100',
    title: 'How to Think 10x Bigger',
    source: 'Tim Ferriss Show · Astro Teller Interview',
    body:
      'Astro Teller, CEO of X, explains why targeting a 10x improvement often yields better results than a 10% one — because it forces you to rethink the entire problem.',
  },
];

export default function Home() {
  const { status } = useSession();
  const signedIn = status === 'authenticated';

  return (
    <MarketingLayout
      title="10X Planner App | Free | 10xplannerapp.com"
      description="A free daily planning app built on 10x thinking. Set ambitious goals, track daily targets, and log your wins. Free to use — no credit card required."
      canonical="/"
    >
      <div className="my-8 mx-auto max-w-screen-xl px-4 sm:my-12 sm:px-6 md:my-20 xl:my-24">
        <div className="text-center">
          <h1 className="text-4xl tracking-tight leading-10 font-extrabold text-gray-900 sm:text-5xl sm:leading-none md:text-6xl">
            Your daily plan to achieve
            <br className="hidden md:block" />
            <span className="text-blue-800"> 10x results</span>
          </h1>
          <p className="mt-3 max-w-md mx-auto text-base text-gray-500 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
            A free daily planning system built on a simple idea: aiming for 10x forces you to
            think differently, prioritize ruthlessly, and actually move the needle.
          </p>
          <div className="mt-5 max-w-md mx-auto md:mt-8">
            <div className="rounded-md shadow mx-auto" style={{ maxWidth: 320 }}>
              <Link
                href={signedIn ? '/planner' : '/login'}
                className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base leading-6 font-medium rounded-md text-white bg-blue-800 hover:bg-blue-900 focus:outline-none focus:border-blue-900 transition duration-150 ease-in-out md:py-4 md:text-lg md:px-10"
              >
                {signedIn ? 'Go to your planner' : 'Start planning free'}
              </Link>
            </div>
            {!signedIn && (
              <p className="text-sm mt-2 text-gray-500">Free to use — no credit card required</p>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:-mt-16">
        <Image
          className="relative mx-auto"
          src="/images/video.gif"
          alt="10X Planner app demo"
          width={1200}
          height={750}
          priority
          unoptimized
        />
      </div>

      <div className="py-16 bg-gray-50 overflow-hidden lg:py-24">
        <div className="relative max-w-xl mx-auto px-4 sm:px-6 lg:px-8 lg:max-w-7xl">
          <svg className="hidden lg:block absolute left-full transform -translate-x-1/2 -translate-y-1/4" width="404" height="784" fill="none" viewBox="0 0 404 784" aria-hidden="true">
            <defs>
              <pattern id="b1e6e422-73f8-40a6-b5d9-c8586e37e0e7" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                <rect x="0" y="0" width="4" height="4" className="text-gray-200" fill="currentColor" />
              </pattern>
            </defs>
            <rect width="404" height="784" fill="url(#b1e6e422-73f8-40a6-b5d9-c8586e37e0e7)" />
          </svg>
          <div className="relative">
            <h2 className="text-center text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Five minutes that shape your entire day
            </h2>
            <p className="mt-4 max-w-3xl mx-auto text-center text-xl text-gray-500">
              A structured daily ritual that keeps your biggest goals front and center — not buried
              in a task list.
            </p>
          </div>

          <div className="relative mt-12 lg:mt-24 lg:grid lg:grid-cols-2 lg:gap-8 lg:items-center">
            <div className="relative">
              <h3 className="text-2xl font-extrabold text-gray-900 tracking-tight sm:text-3xl">
                Think bigger, plan smarter
              </h3>
              <dl className="mt-10 space-y-10">
                {FEATURES.map((f) => (
                  <div key={f.number} className="flex">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-12 w-12 rounded-md bg-blue-800 text-white font-bold text-2xl">
                        {f.number}
                      </div>
                    </div>
                    <div className="ml-4">
                      <dt className="text-lg leading-6 font-medium text-gray-900">{f.title}</dt>
                      <dd className="mt-2 text-base text-gray-500">{f.body}</dd>
                    </div>
                  </div>
                ))}
              </dl>
            </div>
            <div className="mt-12 -mx-4 relative lg:mt-0" aria-hidden="true">
              <svg className="absolute left-1/2 transform -translate-x-1/2 translate-y-16 lg:hidden" width="784" height="404" fill="none" viewBox="0 0 784 404">
                <defs>
                  <pattern id="ca9667ae-9f92-4be7-abcb-9e3d727f2941" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                    <rect x="0" y="0" width="4" height="4" className="text-gray-200" fill="currentColor" />
                  </pattern>
                </defs>
                <rect width="784" height="404" fill="url(#ca9667ae-9f92-4be7-abcb-9e3d727f2941)" />
              </svg>
              <Image
                className="relative mx-auto mt-10 rounded-2xl shadow-lg"
                src="/images/planner-mobile-screenshot.png"
                alt="10X Planner mobile screenshot"
                width={490}
                height={1000}
              />
            </div>
          </div>

          <svg className="hidden lg:block absolute right-full transform translate-x-1/2 translate-y-12" width="404" height="784" fill="none" viewBox="0 0 404 784" aria-hidden="true">
            <defs>
              <pattern id="64e643ad-2176-4f86-b3d7-f2c5da3b6a6d" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                <rect x="0" y="0" width="4" height="4" className="text-gray-200" fill="currentColor" />
              </pattern>
            </defs>
            <rect width="404" height="784" fill="url(#64e643ad-2176-4f86-b3d7-f2c5da3b6a6d)" />
          </svg>
          <div className="relative mt-12 sm:mt-16 lg:mt-24">
            <div className="lg:grid lg:grid-flow-row-dense lg:grid-cols-2 lg:gap-8 lg:items-center">
              <div className="lg:col-start-2">
                <h3 className="text-2xl font-extrabold text-gray-900 tracking-tight sm:text-3xl">
                  Your progress, always accessible
                </h3>
                <p className="mt-3 text-lg text-gray-500">
                  Building new habits takes consistency. Your 10X Planner keeps a complete record of
                  every day&rsquo;s goals, targets, and wins — so you can see how far you&rsquo;ve
                  come and stay motivated to keep going.
                </p>
                <dl className="mt-10 space-y-10">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-12 w-12 rounded-md bg-blue-800 text-white">
                        <svg
                          className="h-6 w-6"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                          />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-4">
                      <dt className="text-lg leading-6 font-medium text-gray-900">
                        Full history, anytime
                      </dt>
                      <dd className="mt-2 text-base text-gray-500">
                        Look back on a year of planning — or three years, or five. Your entire
                        history is saved and organized, accessible from any device. The free plan
                        shows your last 30 days; Pro unlocks everything.
                      </dd>
                    </div>
                  </div>
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-12 w-12 rounded-md bg-blue-800 text-white">
                        <svg
                          className="h-6 w-6"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                          />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-4">
                      <dt className="text-lg leading-6 font-medium text-gray-900">
                        Daily achievement tracking
                      </dt>
                      <dd className="mt-2 text-base text-gray-500">
                        See what you accomplished on any given day. Your goals, targets, and wins
                        form a record that keeps you motivated and accountable.
                      </dd>
                    </div>
                  </div>
                </dl>
              </div>
              <div className="mt-10 -mx-4 relative lg:mt-0 lg:col-start-1">
                <svg className="absolute left-1/2 transform -translate-x-1/2 translate-y-16 lg:hidden" width="784" height="404" fill="none" viewBox="0 0 784 404" aria-hidden="true">
                  <defs>
                    <pattern id="e80155a9-dfde-425a-b5ea-1f6fadd20131" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                      <rect x="0" y="0" width="4" height="4" className="text-gray-200" fill="currentColor" />
                    </pattern>
                  </defs>
                  <rect width="784" height="404" fill="url(#e80155a9-dfde-425a-b5ea-1f6fadd20131)" />
                </svg>
                <Image
                  className="relative mx-auto"
                  src="/images/history-screenshot.png"
                  alt="Journal history screenshot"
                  width={490}
                  height={700}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="py-16 bg-white overflow-hidden lg:py-24">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Fuel for 10x thinkers
            </h2>
            <p className="mt-4 max-w-3xl mx-auto text-xl text-gray-500">
              The best writing on ambitious goal-setting, exponential thinking, and why aiming 10x
              higher changes how you approach everything.
            </p>
          </div>
          <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {READING_LIST.map((item) => (
              <a
                key={item.href}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group block rounded-lg border border-gray-200 p-6 hover:border-blue-800 hover:shadow-md transition"
              >
                <span
                  className={`inline-block px-2 py-1 text-xs font-semibold uppercase tracking-wide rounded ${item.badgeClasses}`}
                >
                  {item.badge}
                </span>
                <h3 className="mt-3 text-lg font-bold text-gray-900 group-hover:text-blue-800">
                  {item.title}
                </h3>
                <p className="mt-1 text-sm text-gray-500">{item.source}</p>
                <p className="mt-2 text-sm text-gray-500">{item.body}</p>
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="py-12 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 sm:p-10">
            <h2 className="text-2xl font-extrabold text-gray-900">
              Wait — is this Grant Cardone&rsquo;s 10X Planner?
            </h2>
            <p className="mt-2 text-xl font-bold text-blue-800">No. It&rsquo;s 10x better.</p>
            <p className="mt-4 text-base text-gray-500">
              The 10X Planner App is an independent product. It is not affiliated with, endorsed
              by, or connected to Grant Cardone, Cardone Training Technologies, or any related
              entities.
            </p>
            <p className="mt-3 text-base text-gray-500">
              The concept of &ldquo;10x&rdquo; — meaning <em>tenfold improvement</em> — has been a
              cornerstone of ambitious thinking for decades, spanning fields from{' '}
              <a href="https://antirez.com/news/112" className="text-blue-800 hover:underline">
                software engineering
              </a>{' '}
              to{' '}
              <a
                href="https://blog.google/alphabet/we-choose-go-moon-lessons-x-spirit-original-moonshot"
                className="text-blue-800 hover:underline"
              >
                Google&rsquo;s moonshot labs
              </a>{' '}
              to{' '}
              <a
                href="https://superframeworks.com/blog/10x-easier-than-2x"
                className="text-blue-800 hover:underline"
              >
                entrepreneurial strategy
              </a>
              . This is a universal idea: if you aim ten times higher, you think ten times
              differently.
            </p>
            <ul className="mt-6 space-y-3">
              <li className="flex items-start">
                <span className="text-blue-800 font-bold mr-2">&rarr;</span>
                <span className="text-gray-600">
                  We&rsquo;re digital-first — no paper to lose, accessible from any device
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-800 font-bold mr-2">&rarr;</span>
                <span className="text-gray-600">
                  Your full history is searchable and always available
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-800 font-bold mr-2">&rarr;</span>
                <span className="text-gray-600">
                  Built by people who actually use daily planning systems
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-800 font-bold mr-2">&rarr;</span>
                <span className="text-gray-600">
                  Free to use — no credit card required
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="py-12 bg-blue-50">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between">
          <div>
            <h2 className="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              <span className="block">Ready to plan with purpose?</span>
              <span className="block text-blue-800">
                Create a free account and start today.
              </span>
            </h2>
          </div>
          <div className="mt-8 flex lg:mt-0 lg:flex-shrink-0">
            <div className="inline-flex rounded-md shadow">
              <Link
                href={signedIn ? '/planner' : '/login'}
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-800 hover:bg-blue-900"
              >
                {signedIn ? 'Go to your planner' : 'Start planning free'}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </MarketingLayout>
  );
}
